import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => string;
      reset: (id: string) => void;
      remove: (id: string) => void;
    };
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** How long a submit waits for a background check to finish before giving up. */
const TOKEN_WAIT_MS = 8_000;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Turnstile failed to load'));
    document.head.appendChild(s);
  });
}

type WidgetProps = {
  onToken: (token: string | null) => void;
  onError: () => void;
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
  widgetIdRef: RefObject<string | null>;
};

/**
 * State for one Turnstile widget. In Cloudflare's managed/invisible modes the
 * check usually runs with nothing on screen, so a fast user can submit before
 * the token exists: `getToken()` waits for it (null on timeout) instead of
 * failing straight away. Once the check has failed (script blocked, widget
 * error) it resolves null at once rather than making the user wait. Tokens are
 * single use — call `reset()` after any attempt that sent one and did not
 * leave the form. `onReady` runs whenever a fresh token arrives, e.g. to clear
 * a "complete the check" error once the user has.
 */
export function useTurnstile({ onReady }: { onReady?: () => void } = {}) {
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const token = useRef<string | null>(null);
  const waiters = useRef(new Set<(t: string | null) => void>());
  const failed = useRef(false);
  const widgetIdRef = useRef<string | null>(null);
  const visibleRef = useRef(false);
  const [visible, setVisibleState] = useState(false);
  const setVisible = useCallback((v: boolean) => {
    visibleRef.current = v;
    setVisibleState(v);
  }, []);

  const onToken = useCallback((t: string | null) => {
    token.current = t;
    failed.current = false;
    if (!t) return;
    for (const done of [...waiters.current]) done(t);
    onReadyRef.current?.();
  }, []);

  const onError = useCallback(() => {
    token.current = null;
    failed.current = true;
    for (const done of [...waiters.current]) done(null);
  }, []);

  const getToken = useCallback((): Promise<string | null> => {
    if (token.current) return Promise.resolve(token.current);
    if (failed.current) return Promise.resolve(null);
    return new Promise((resolve) => {
      const done = (t: string | null) => {
        clearTimeout(timer);
        waiters.current.delete(done);
        resolve(t);
      };
      const timer = setTimeout(() => done(null), TOKEN_WAIT_MS);
      waiters.current.add(done);
    });
  }, []);

  const reset = useCallback(() => {
    token.current = null;
    failed.current = false;
    if (widgetIdRef.current && window.turnstile) window.turnstile.reset(widgetIdRef.current);
  }, []);

  /**
   * Copy for a submit whose `getToken()` came back null: point at the widget
   * when there is one to complete, otherwise the background check just failed.
   */
  const failureMessage = useCallback(
    () =>
      visibleRef.current
        ? 'Please complete the security check.'
        : "The security check didn't finish. Try again in a moment.",
    [],
  );

  const widgetProps: WidgetProps = {
    onToken,
    onError,
    visible,
    onVisibleChange: setVisible,
    widgetIdRef,
  };
  return { visible, getToken, reset, failureMessage, widgetProps };
}

/**
 * Renders the Turnstile widget. While it has no height (invisible mode, or
 * before it loads) it is taken out of the layout so the parent's gap doesn't
 * leave a hole, and comes back the moment Cloudflare shows a challenge.
 */
export function Turnstile({
  siteKey,
  onToken,
  onError,
  visible,
  onVisibleChange,
  widgetIdRef,
}: { siteKey: string } & WidgetProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) =>
      onVisibleChange((entry?.contentRect.height ?? 0) > 0),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onVisibleChange]);

  useEffect(() => {
    let widgetId: string | null = null;
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetId = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(null),
          'error-callback': onError,
        });
        widgetIdRef.current = widgetId;
      })
      .catch(onError);
    return () => {
      cancelled = true;
      widgetIdRef.current = null;
      // A token from a removed widget must not be handed to the next one.
      onToken(null);
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [siteKey, onToken, onError, widgetIdRef]);

  // The outer box is clipped with sr-only (not display:none) while empty; the
  // inner render target keeps its natural height, so the observer still sees
  // a challenge appear.
  return (
    <div className={visible ? undefined : 'sr-only'}>
      <div ref={ref} />
    </div>
  );
}
